import GetScript from './GetScript.js';
import { environment } from './environment.js';

let client = null;
let openSubscriptions = {};
let isShared = false;

export class LightStreamerSubscription {
    static subscribe(_epic, modifier = '', container, onChange, index = 0) {
        let epicValues = 'V2-F-BID,OFR,CPT,CPC,HIG,LOW,UTM,SWAP_1_SHORT,SWAP_1_LONG,SCALING_FACTOR,SWAP_8_LONG,SWAP_8_SHORT';
        if (modifier && modifier.toUpperCase().includes('PUBLIC')) {
            epicValues += ',AUTO_QUALITY';
        }
        const epic = `${epicValues}|${_epic}|1`;
        const schema = epicValues.split(',');
        const subscription = new window.Lightstreamer.Subscription('MERGE', epic, schema);
        subscription.addListener(LightStreamerSubscription.subscriptionListener(container, onChange, index));
        subscription.setRequestedMaxFrequency(1);
        LightStreamerSubscription.client.subscribe(subscription);
        openSubscriptions[_epic] = subscription;
    }

    static subscriptionListener(container, onChange, index) {
        return {
            onItemUpdate(itemUpdate) {
                itemUpdate.forEachChangedField((fieldName, fieldPos, newValue) => {
                    onChange(container, fieldName, fieldPos, newValue, index);
                });
            },
        }
    }

    static unsubscribe(epic) {
        if (openSubscriptions[epic]) {
            LightStreamerSubscription.client.unsubscribe(openSubscriptions[epic]);
            delete openSubscriptions[epic];
        }
    }

    static get client() { return client; }

    static set client(_client) { client = _client; }

    static isConnected() {
        if (!client || typeof client.getStatus !== 'function') {
            return false;
        }
        return client.getStatus().startsWith('CONNECTED');
    }

    static domain(hostname = window.location.hostname) {
        switch (environment(hostname)) {
            case 'net':
                return 'net-';
            case 'web':
                return 'web-';
            default:
                return '';
        }
    }

    static connect() {
        // Return existing connection if available
        if (this.client && this.isConnected()) {
            return Promise.resolve(this.client);
        }
        
        // Check if Lightstreamer is available
        if (typeof window.Lightstreamer === 'undefined') {
            return Promise.reject(new Error('Lightstreamer library is not loaded'));
        }
        
        return new Promise((resolve, reject) => {
            try {
                this.client = new window.Lightstreamer.LightstreamerClient(
                    `https://${LightStreamerSubscription.domain()}upp.ig.com`, 
                    'InVisionProvider'
                );
                
                // Set up connection options
                this.client.connectionOptions.setRetryDelay(2000);
                this.client.connectionOptions.setReconnectTimeout(10000);
                
                let connectionResolved = false;
                
                this.client.addListener({
                    onStatusChange: (status) => {
                        
                        if (status.startsWith('CONNECTED') && !connectionResolved) {
                            isShared = true;
                            connectionResolved = true;
                            resolve(this.client);
                        } else if (status === 'DISCONNECTED') {
                            isShared = false;
                            if (!connectionResolved) {
                                connectionResolved = true;
                                reject(new Error('Connection failed'));
                            }
                        }
                    },
                    
                    onServerError: (errorCode, errorMessage) => {
                        console.error('Server Error:', errorCode, errorMessage);
                        if (!connectionResolved) {
                            connectionResolved = true;
                            reject(new Error(`Server Error ${errorCode}: ${errorMessage}`));
                        }
                    }
                });
                
                this.client.connect();
                client = this.client; // Update the module-level variable
                
                // Timeout after 30 seconds
                setTimeout(() => {
                    if (!connectionResolved) {
                        connectionResolved = true;
                        reject(new Error('Connection timeout'));
                    }
                }, 30000);
                
            } catch (error) {
                reject(error);
            }
        });
    }

    static loadScript(setup) {
        if (typeof window.Lightstreamer !== 'undefined' && window.Lightstreamer.version === '9.2.3') {
            LightStreamerSubscription.connect().then(() => {
                setup();
            }).catch(error => {
                console.error('Connection failed:', error);
            });
        } else {
            GetScript.JS(`https://${LightStreamerSubscription.domain()}a.c-dn.net/c/etc.clientlibs/settings/wcm/designs/onedomain/clientlibs_3rdparty_deps/lightstreamer.min.js`)
                .then(() => {
                    // Verify the script loaded properly
                    if (typeof window.Lightstreamer === 'undefined') {
                        throw new Error('Lightstreamer library failed to load properly');
                    }
                    return LightStreamerSubscription.connect();
                })
                .then(() => {
                    setup();
                })
                .catch((error) => {
                    console.error('lightstreamer-9.2.3.js Failed to load or connect!', error);
                });
        }
    }

    static disconnect() {
        if (client && typeof client.disconnect === 'function') {
            client.disconnect();
        }
        client = null;
        isShared = false;
    }

    // Getter for the isShared state
    static get isShared() {
        return isShared;
    }
}